/*
  <kg-reviews>: laedt weitere Bewertungen vom Bewertungsdienst nach, sortiert sie und
  baut sie in das eigene Markup um. Der Abruf laeuft ohne Zugangsdaten ueber die
  oeffentliche Widget-Adresse, die auch das Original-Widget nutzt.

  Weitere Bewertungen kommen seitenweise, je Abruf bis zu 30 Stueck. Nur wenn die
  Sortierung gewechselt wird, kommt der Rest dazu, denn eine Reihenfolge darf nicht
  nur fuer das gerade Sichtbare gelten. Danach laeuft alles ohne weitere Abrufe.
*/

const ENDPUNKT = 'https://judge.me/reviews/reviews_for_widget';
const PRO_SEITE = 30; /* Obergrenze der Gegenstelle, groessere Werte liefern nicht mehr */
const GLEICHZEITIG = 4; /* So viele Seiten laufen parallel, wenn der Rest gebraucht wird */

class KgReviews extends HTMLElement {
  connectedCallback() {
    this.liste = this.querySelector('[data-reviews-list]');
    this.knopf = this.querySelector('[data-reviews-more]');
    this.hinweis = this.querySelector('[data-reviews-note]');
    this.fuss = this.querySelector('[data-reviews-foot]');
    this.fortschritt = this.querySelector('[data-reviews-progress]');
    this.sortierung = this.querySelector('[data-reviews-sort]');
    this.vorlage = this.querySelector('template');
    if (!this.liste || !this.knopf) return;

    this.gesamt = Number(this.dataset.total) || 0;
    this.schritt = this.geraeteZahl();
    this.gezeigt = this.liste.querySelectorAll('.review').length;
    this.daten = [];
    this.seite = 0;
    this.vollstaendig = false;

    this.knopf.addEventListener('click', () => this.mehr());
    this.sortierung?.addEventListener('change', () => this.neuSortieren());
    this.pruefeKnopf();

    this.anpassen();

    /*
      Beim Wechsel der Breite gilt die andere Zahl. Wichtig fuer die Vorschau im Editor: Dort
      springt man zwischen Rechner und Handy, ohne die Seite neu zu laden.
    */
    this.onResize = () => {
      cancelAnimationFrame(this.resizeTakt);
      this.resizeTakt = requestAnimationFrame(() => this.anpassen());
    };
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.resizeTakt);
    window.removeEventListener('resize', this.onResize);
  }

  /*
    Die Zahl fuer diese Breite. Sie steht als `--reviews-limit` im Blatt, das zwischen Rechner
    und Handy umschaltet — damit die 750 px nur dort stehen und nicht auch hier. Fehlt die
    Variable (kein Blatt geladen), gilt die Zahl am Element.
  */
  geraeteZahl() {
    const wert = Number.parseInt(getComputedStyle(this).getPropertyValue('--reviews-limit'), 10);
    if (Number.isFinite(wert) && wert > 0) return wert;
    return Number(this.dataset.limit) || 5;
  }

  /*
    Auf die Zahl dieser Breite bringen: Zu viele im HTML werden entfernt, zu wenige nachgeholt.
    Wer selbst auf „Mehr anzeigen" gedrueckt hat, wird nicht wieder zusammengeschoben — das
    waere eine Unverschaemtheit gegenueber jemandem, der gerade liest.
  */
  async anpassen() {
    if (this.vonHand) return;
    const soll = this.geraeteZahl();
    if (soll === this.schritt && soll === this.liste.querySelectorAll('.review').length) return;
    this.schritt = soll;
    const da = this.liste.querySelectorAll('.review').length;
    if (soll < da) {
      [...this.liste.querySelectorAll('.review')].slice(soll).forEach((el) => el.remove());
      this.gezeigt = soll;
      this.pruefeKnopf();
      return;
    }
    if (soll > da && this.gesamt > da) await this.aufstocken();
  }

  /*
    Aufstocken, wenn mehr verlangt sind als der Server liefern kann: Im Metafeld, aus dem die
    Section ihre erste Seite rendert, stehen fuenf Bewertungen, mehr gibt die Gegenstelle pro
    Seite nicht her. Wer acht sehen will, bekommt die fehlenden drei nach dem Laden nachgeholt.

    Die ersten fuenf stehen weiter im HTML vom Server — ohne Javascript und fuer Suchmaschinen
    bleibt es dabei. Liegt die Zahl bei fuenf oder darunter, passiert gar nichts: kein Abruf
    fuer die Vorgabe. Faellt der Abruf aus, bleibt stehen was da ist.
  */
  async aufstocken() {
    try {
      while (this.daten.length < this.schritt && !this.vollstaendig) await this.naechsteSeite();
      if (!this.daten.length) return;
      this.gezeigt = Math.min(this.schritt, this.daten.length);
      this.zeichnen();
      this.pruefeKnopf();
    } catch {
      /* Kein Netz, kein Schaden. */
    }
  }

  pruefeKnopf() {
    const sichtbar = this.liste.querySelectorAll('.review').length;
    const grenze = this.vollstaendig ? this.daten.length : this.gesamt;
    const fertig = sichtbar >= grenze;
    this.knopf.hidden = fertig;
    if (this.fuss) this.fuss.hidden = fertig;
    if (this.fortschritt) {
      this.fortschritt.textContent = (this.fortschritt.dataset.loaded || '')
        .replace('[shown]', String(sichtbar))
        .replace('[total]', String(grenze));
    }
  }

  async mehr() {
    /* Ab jetzt richtet sich die Liste nicht mehr nach der Breite — hier liest jemand. */
    this.vonHand = true;
    await this.arbeiten(async () => {
      this.gezeigt += this.schritt;
      while (this.daten.length < this.gezeigt && !this.vollstaendig) await this.naechsteSeite();
      this.zeichnen();
    });
  }

  async neuSortieren() {
    await this.arbeiten(async () => {
      await this.restHolen();
      this.zeichnen();
    });
  }

  async naechsteSeite() {
    const teil = await this.holen(this.seite + 1);
    this.seite += 1;
    this.daten.push(...teil.eintraege);
    if (teil.roh < PRO_SEITE || this.seite * PRO_SEITE >= this.gesamt) this.vollstaendig = true;
  }

  /* Die Sortierung braucht den ganzen Bestand, also kommen die fehlenden
     Seiten in kleinen Gruppen parallel statt eine nach der anderen. */
  async restHolen() {
    if (this.vollstaendig) return;
    const letzte = Math.max(1, Math.ceil(this.gesamt / PRO_SEITE));
    for (let start = this.seite + 1; start <= letzte; start += GLEICHZEITIG) {
      const nummern = [];
      for (let n = start; n < start + GLEICHZEITIG && n <= letzte; n += 1) nummern.push(n);
      const teile = await Promise.all(nummern.map((n) => this.holen(n)));
      for (const teil of teile) this.daten.push(...teil.eintraege);
      this.seite = nummern[nummern.length - 1];
    }
    this.vollstaendig = true;
  }

  /* Gemeinsamer Rahmen: doppelte Laeufe verhindern, Wartezustand zeigen, Fehler melden */
  async arbeiten(aufgabe) {
    if (this.laeuft) return;
    this.laeuft = true;
    this.knopf.disabled = true;
    this.knopf.setAttribute('aria-busy', 'true');
    this.setAttribute('aria-busy', 'true');
    if (this.hinweis) this.hinweis.hidden = true;

    try {
      await aufgabe();
    } catch {
      if (this.hinweis) this.hinweis.hidden = false;
    } finally {
      this.laeuft = false;
      this.knopf.disabled = false;
      this.knopf.removeAttribute('aria-busy');
      this.removeAttribute('aria-busy');
      this.pruefeKnopf();
    }
  }

  async holen(seite) {
    const adresse = new URL(ENDPUNKT);
    adresse.searchParams.set('url', this.dataset.shop);
    adresse.searchParams.set('shop_domain', this.dataset.shop);
    adresse.searchParams.set('platform', 'shopify');
    adresse.searchParams.set('product_id', this.dataset.product);
    adresse.searchParams.set('page', String(seite));
    adresse.searchParams.set('per_page', String(PRO_SEITE));

    const antwort = await fetch(adresse, { headers: { Accept: 'application/json' } });
    if (!antwort.ok) throw new Error(String(antwort.status));
    const daten = await antwort.json();
    if (daten.total_count) this.gesamt = Number(daten.total_count);

    const doc = new DOMParser().parseFromString(daten.html || '', 'text/html');
    const roh = [...doc.querySelectorAll('.jdgm-rev')];
    /* Bewertungen ohne Titel und Text bleiben aus, genau wie in der Ausgabe am Server */
    return { roh: roh.length, eintraege: roh.map((eintrag) => this.lesen(eintrag)).filter((satz) => satz.titel || satz.text) };
  }

  /* Aus dem fremden Markup werden reine Angaben */
  lesen(eintrag) {
    const stempel = eintrag.querySelector('[data-content]')?.dataset.content || '';
    return {
      note: Number(eintrag.querySelector('[data-score]')?.dataset.score || 0),
      datum: stempel.split(' ')[0],
      autor: eintrag.querySelector('.jdgm-rev__author')?.textContent.trim() || '',
      titel: eintrag.querySelector('.jdgm-rev__title')?.textContent.trim() || '',
      text: eintrag.querySelector('.jdgm-rev__body')?.textContent.trim() || '',
      geprueft: eintrag.dataset.verifiedBuyer === 'true',
      angepinnt: Boolean(eintrag.querySelector('.jdgm-rev__pinned')),
    };
  }

  sortiert() {
    const art = this.sortierung?.value || 'recent';
    /* Bewertungen ohne Datum ans Ende, sonst wandern sie bei jeder Sortierung nach oben */
    const nachDatum = (a, b) => (b.datum || '').localeCompare(a.datum || '');
    const reihen = {
      recent: nachDatum,
      oldest: (a, b) => (a.datum || '9999').localeCompare(b.datum || '9999'),
      highest: (a, b) => b.note - a.note || nachDatum(a, b),
      lowest: (a, b) => a.note - b.note || nachDatum(a, b),
    };
    /* Angepinnte Bewertungen bleiben in jeder Reihenfolge oben, das ist der Sinn des Anpinnens */
    const basis = reihen[art] || nachDatum;
    return [...this.daten].sort((a, b) => Number(b.angepinnt) - Number(a.angepinnt) || basis(a, b));
  }

  zeichnen() {
    const sichtbar = this.sortiert().slice(0, this.gezeigt);
    this.liste.classList.toggle('reviews__list--pinned', sichtbar.some((satz) => satz.angepinnt));
    this.liste.replaceChildren(...sichtbar.map((satz) => this.bauen(satz)));
  }

  bauen(satz) {
    const knoten = this.vorlage.content.firstElementChild.cloneNode(true);
    knoten.querySelector('.rating').style.setProperty('--rating', String(satz.note));

    const zeit = knoten.querySelector('.review__date');
    if (satz.datum) {
      zeit.dateTime = satz.datum;
      zeit.textContent = this.datumText(satz.datum);
    } else {
      zeit.remove();
    }

    const titelKnoten = knoten.querySelector('.review__title');
    if (satz.titel) titelKnoten.textContent = satz.titel;
    else titelKnoten.remove();

    const textKnoten = knoten.querySelector('.review__body');
    if (satz.text) textKnoten.textContent = satz.text;
    else textKnoten.remove();

    knoten.querySelector('[data-review-author]').textContent = satz.autor || this.dataset.anonymous || '';
    if (!satz.geprueft) knoten.querySelector('.review__verified')?.remove();
    if (!satz.angepinnt) knoten.querySelector('.review__pin')?.remove();
    return knoten;
  }

  datumText(datum) {
    const teile = datum.split('-');
    if (teile.length !== 3) return datum;
    /* Dasselbe Format wie die serverseitig gerenderten Bewertungen */
    return this.dataset.dateFormat === 'month' ? `${teile[1]}/${teile[0]}` : `${teile[2]}.${teile[1]}.${teile[0]}`;
  }
}

customElements.define('kg-reviews', KgReviews);
